import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Code,
  Divider,
  Group,
  Modal,
  Pill,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconSearch,
  IconShieldCheck,
} from "@tabler/icons-react";
import { useUpdateCommunicationRedirectSettings } from "../../../hooks/admin/useCommunicationRedirectSettings";
import {
  CommunicationBypassPrefix,
  CommunicationRedirectSettings,
  CommunicationSettingsConflictError,
} from "../../../services/admin/communicationSettings.service";
import BypassPrefixOptionRow from "./BypassPrefixOptionRow";
import ConfirmBypassPrefixChangesModal from "./ConfirmBypassPrefixChangesModal";
import DiscardBypassPrefixChangesModal from "./DiscardBypassPrefixChangesModal";
import SelectedBypassPrefixPill from "./SelectedBypassPrefixPill";

interface RedirectBypassPrefixEditorProps {
  opened: boolean;
  settings: CommunicationRedirectSettings;
  onClose: () => void;
  onReload: () => void;
}

function comparePrefixCodes(
  left: CommunicationBypassPrefix,
  right: CommunicationBypassPrefix
): number {
  return left.code.localeCompare(right.code);
}

function mergePrefixes(
  available: CommunicationBypassPrefix[],
  selected: CommunicationBypassPrefix[]
): CommunicationBypassPrefix[] {
  const prefixes = new Map<string, CommunicationBypassPrefix>();
  available.forEach((prefix) => prefixes.set(prefix.id, prefix));
  selected.forEach((prefix) => prefixes.set(prefix.id, prefix));
  return Array.from(prefixes.values()).sort(comparePrefixCodes);
}

function samePrefixSet(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;
  const rightSet = new Set(right);
  return left.every((prefixId) => rightSet.has(prefixId));
}

export default function RedirectBypassPrefixEditor({
  opened,
  settings,
  onClose,
  onReload,
}: RedirectBypassPrefixEditorProps) {
  const initialPrefixIds = useMemo(
    () => settings.selectedPrefixes.map((prefix) => prefix.id),
    [settings.selectedPrefixes]
  );
  const [selectedPrefixIds, setSelectedPrefixIds] = useState(initialPrefixIds);
  const [search, setSearch] = useState("");
  const [confirmOpened, setConfirmOpened] = useState(false);
  const [discardOpened, setDiscardOpened] = useState(false);
  const updateSettings = useUpdateCommunicationRedirectSettings();

  const allPrefixes = useMemo(
    () => mergePrefixes(settings.availablePrefixes, settings.selectedPrefixes),
    [settings.availablePrefixes, settings.selectedPrefixes]
  );
  const selectedIdSet = useMemo(
    () => new Set(selectedPrefixIds),
    [selectedPrefixIds]
  );
  const initialIdSet = useMemo(
    () => new Set(initialPrefixIds),
    [initialPrefixIds]
  );
  const selectedPrefixes = useMemo(
    () => allPrefixes.filter((prefix) => selectedIdSet.has(prefix.id)),
    [allPrefixes, selectedIdSet]
  );
  const additions = useMemo(
    () => selectedPrefixes.filter((prefix) => !initialIdSet.has(prefix.id)),
    [initialIdSet, selectedPrefixes]
  );
  const removals = useMemo(
    () =>
      settings.selectedPrefixes
        .filter((prefix) => !selectedIdSet.has(prefix.id))
        .sort(comparePrefixCodes),
    [selectedIdSet, settings.selectedPrefixes]
  );
  const filteredPrefixes = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return allPrefixes;
    return allPrefixes.filter((prefix) =>
      prefix.code.toLowerCase().includes(normalizedSearch)
    );
  }, [allPrefixes, search]);
  const isDirty = useMemo(
    () => !samePrefixSet(initialPrefixIds, selectedPrefixIds),
    [initialPrefixIds, selectedPrefixIds]
  );
  const isConflict = useMemo(
    () => updateSettings.error instanceof CommunicationSettingsConflictError,
    [updateSettings.error]
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.currentTarget.value);
    },
    []
  );
  const handleToggle = useCallback((prefixId: string, checked: boolean) => {
    setSelectedPrefixIds((current) => {
      if (checked) return Array.from(new Set([...current, prefixId]));
      return current.filter((currentId) => currentId !== prefixId);
    });
  }, []);
  const handleRemove = useCallback(
    (prefixId: string) => handleToggle(prefixId, false),
    [handleToggle]
  );
  const handleRequestClose = useCallback(() => {
    if (updateSettings.isPending) return;
    if (isDirty) setDiscardOpened(true);
    else onClose();
  }, [isDirty, onClose, updateSettings.isPending]);
  const handleCancelDiscard = useCallback(() => setDiscardOpened(false), []);
  const handleDiscard = useCallback(() => {
    setDiscardOpened(false);
    onClose();
  }, [onClose]);
  const handleReviewChanges = useCallback(() => setConfirmOpened(true), []);
  const handleCloseConfirmation = useCallback(
    () => setConfirmOpened(false),
    []
  );
  const handleSaveSuccess = useCallback(() => {
    notifications.show({
      title: "Redirect bypass prefixes updated",
      message: "The saved prefix set now applies to new sends and retries.",
      color: "green",
      autoClose: 4000,
    });
    setConfirmOpened(false);
    onClose();
  }, [onClose]);
  const handleSaveError = useCallback(() => {
    setConfirmOpened(false);
  }, []);
  const handleConfirmSave = useCallback(() => {
    updateSettings.mutate(
      {
        prefixIds: selectedPrefixIds,
        expectedRevision: settings.revision,
      },
      { onSuccess: handleSaveSuccess, onError: handleSaveError }
    );
  }, [
    handleSaveError,
    handleSaveSuccess,
    selectedPrefixIds,
    settings.revision,
    updateSettings,
  ]);
  const handleReload = useCallback(() => {
    updateSettings.reset();
    setConfirmOpened(false);
    onReload();
  }, [onReload, updateSettings]);

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleRequestClose}
        title="Redirect bypass prefixes"
        size="lg"
        centered
        closeOnClickOutside={!updateSettings.isPending}
        closeOnEscape={!updateSettings.isPending}
      >
        <Stack gap="md">
          <Alert
            icon={<IconShieldCheck size={18} />}
            color="blue"
            variant="light"
            title="Selected prefixes may communicate with real recipients"
          >
            Case context is authoritative. Entity mappings are used only when a
            communication has no persisted case prefix.
          </Alert>

          {isConflict && (
            <Alert
              icon={<IconAlertTriangle size={18} />}
              color="orange"
              variant="light"
              title="Settings changed in another session"
              role="alert"
            >
              <Stack gap="sm">
                <Text size="sm">
                  Reload the latest settings before making more changes.
                </Text>
                <Button size="xs" variant="light" onClick={handleReload}>
                  Reload latest settings
                </Button>
              </Stack>
            </Alert>
          )}

          {updateSettings.isError && !isConflict && (
            <Alert
              icon={<IconAlertTriangle size={18} />}
              color="red"
              variant="light"
              title="Could not save redirect settings"
              role="alert"
            >
              {updateSettings.error.message}
            </Alert>
          )}

          <Stack gap="xs">
            <Text size="sm" fw={600}>
              Selected prefixes ({selectedPrefixes.length})
            </Text>
            {selectedPrefixes.length === 0 ? (
              <Text size="sm" c="dimmed">
                No prefixes selected. Redirect safeguards apply to all
                contextual communications.
              </Text>
            ) : (
              <Pill.Group>
                {selectedPrefixes.map((prefix) => (
                  <SelectedBypassPrefixPill
                    key={prefix.id}
                    prefix={prefix}
                    onRemove={handleRemove}
                  />
                ))}
              </Pill.Group>
            )}
          </Stack>

          <Divider />

          <TextInput
            label="Find a prefix"
            placeholder="Search by prefix code"
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={handleSearchChange}
            data-autofocus
          />

          <ScrollArea.Autosize mah="45vh" offsetScrollbars type="auto">
            <Stack gap="xs" pr="xs">
              {filteredPrefixes.length === 0 ? (
                <Text size="sm" c="dimmed" ta="center" py="lg">
                  {allPrefixes.length === 0
                    ? "No prefixes are available."
                    : "No prefixes match your search."}
                </Text>
              ) : (
                filteredPrefixes.map((prefix) => (
                  <BypassPrefixOptionRow
                    key={prefix.id}
                    prefix={prefix}
                    checked={selectedIdSet.has(prefix.id)}
                    onToggle={handleToggle}
                  />
                ))
              )}
            </Stack>
          </ScrollArea.Autosize>

          <Alert color="gray" variant="light" title="Static email exemptions">
            <Stack gap={4}>
              <Text size="xs">
                Addresses: {settings.staticEmailExemptions.addresses.length};
                domains: {settings.staticEmailExemptions.domains.length}
              </Text>
              {settings.staticEmailExemptions.addresses.length > 0 && (
                <Code block>
                  {settings.staticEmailExemptions.addresses.join("\n")}
                </Code>
              )}
              {settings.staticEmailExemptions.domains.length > 0 && (
                <Code block>
                  {settings.staticEmailExemptions.domains.join("\n")}
                </Code>
              )}
            </Stack>
          </Alert>

          <Group justify="space-between" align="center">
            <Text size="xs" c="dimmed">
              Revision {settings.revision}
              {settings.updatedBy
                ? ` · updated by ${settings.updatedBy.name}`
                : ""}
            </Text>
            <Group gap="sm">
              <Button variant="default" onClick={handleRequestClose}>
                Cancel
              </Button>
              <Button
                color="orange"
                onClick={handleReviewChanges}
                disabled={!isDirty || updateSettings.isPending || isConflict}
              >
                Review changes
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>

      <ConfirmBypassPrefixChangesModal
        opened={confirmOpened}
        additions={additions}
        removals={removals}
        isSaving={updateSettings.isPending}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmSave}
      />
      <DiscardBypassPrefixChangesModal
        opened={discardOpened}
        onCancel={handleCancelDiscard}
        onDiscard={handleDiscard}
      />
    </>
  );
}
